import { AuthProvider, UserRole } from '../types/auth';
export { AuthProvider } from '../types/auth';
export const defaultAuthConfig = {
    allowMultipleProviders: true,
    sessionTimeout: 480,
    refreshTokenRotation: true,
    requireEmailVerification: false,
    autoCreateUsers: true,
    defaultUserRole: UserRole.DEVELOPER,
    jwtExpiresIn: '1h',
    refreshTokenExpiresIn: '7d',
};
export const AUTH_ENV_VARS = {
    JWT_SECRET: 'JWT_SECRET',
    JWT_EXPIRES_IN: 'JWT_EXPIRES_IN',
    REFRESH_TOKEN_EXPIRES_IN: 'REFRESH_TOKEN_EXPIRES_IN',
    SESSION_TIMEOUT: 'SESSION_TIMEOUT',
    DEFAULT_USER_ROLE: 'DEFAULT_USER_ROLE',
    AUTO_CREATE_USERS: 'AUTO_CREATE_USERS',
    REQUIRE_EMAIL_VERIFICATION: 'REQUIRE_EMAIL_VERIFICATION',
    AZURE_AD_CLIENT_ID: 'AZURE_AD_CLIENT_ID',
    AZURE_AD_CLIENT_SECRET: 'AZURE_AD_CLIENT_SECRET',
    AZURE_AD_TENANT_ID: 'AZURE_AD_TENANT_ID',
    AZURE_AD_AUTHORITY: 'AZURE_AD_AUTHORITY',
    AZURE_AD_REDIRECT_URI: 'AZURE_AD_REDIRECT_URI',
    AZURE_AD_POST_LOGOUT_REDIRECT_URI: 'AZURE_AD_POST_LOGOUT_REDIRECT_URI',
    AZURE_AD_SCOPES: 'AZURE_AD_SCOPES',
    AZURE_AD_ENABLED: 'AZURE_AD_ENABLED',
    AZURE_B2C_CLIENT_ID: 'AZURE_B2C_CLIENT_ID',
    AZURE_B2C_CLIENT_SECRET: 'AZURE_B2C_CLIENT_SECRET',
    AZURE_B2C_TENANT_ID: 'AZURE_B2C_TENANT_ID',
    AZURE_B2C_AUTHORITY: 'AZURE_B2C_AUTHORITY',
    AZURE_B2C_POLICY: 'AZURE_B2C_POLICY',
    AZURE_B2C_REDIRECT_URI: 'AZURE_B2C_REDIRECT_URI',
    AZURE_B2C_SCOPES: 'AZURE_B2C_SCOPES',
    AZURE_B2C_ENABLED: 'AZURE_B2C_ENABLED',
    GOOGLE_CLIENT_ID: 'GOOGLE_CLIENT_ID',
    GOOGLE_CLIENT_SECRET: 'GOOGLE_CLIENT_SECRET',
    GOOGLE_REDIRECT_URI: 'GOOGLE_REDIRECT_URI',
    GOOGLE_HOSTED_DOMAIN: 'GOOGLE_HOSTED_DOMAIN',
    GOOGLE_ENABLED: 'GOOGLE_ENABLED',
    GITHUB_CLIENT_ID: 'GITHUB_CLIENT_ID',
    GITHUB_CLIENT_SECRET: 'GITHUB_CLIENT_SECRET',
    GITHUB_REDIRECT_URI: 'GITHUB_REDIRECT_URI',
    GITHUB_ALLOW_SIGNUP: 'GITHUB_ALLOW_SIGNUP',
    GITHUB_ENABLED: 'GITHUB_ENABLED',
    OKTA_CLIENT_ID: 'OKTA_CLIENT_ID',
    OKTA_CLIENT_SECRET: 'OKTA_CLIENT_SECRET',
    OKTA_ORG_URL: 'OKTA_ORG_URL',
    OKTA_REDIRECT_URI: 'OKTA_REDIRECT_URI',
    OKTA_ENABLED: 'OKTA_ENABLED',
    AUTH0_CLIENT_ID: 'AUTH0_CLIENT_ID',
    AUTH0_CLIENT_SECRET: 'AUTH0_CLIENT_SECRET',
    AUTH0_DOMAIN: 'AUTH0_DOMAIN',
    AUTH0_AUDIENCE: 'AUTH0_AUDIENCE',
    AUTH0_REDIRECT_URI: 'AUTH0_REDIRECT_URI',
    AUTH0_ENABLED: 'AUTH0_ENABLED',
};
function getEnvVar(key, defaultValue) {
    if (typeof process === 'undefined') {
        return defaultValue;
    }
    return process.env[key] || defaultValue;
}
function getEnvBool(key, defaultValue = false) {
    if (typeof process === 'undefined') {
        return defaultValue;
    }
    const value = process.env[key];
    if (!value)
        return defaultValue;
    return value.toLowerCase() === 'true' || value === '1';
}
function getEnvNumber(key, defaultValue) {
    if (typeof process === 'undefined') {
        return defaultValue;
    }
    const value = process.env[key];
    if (!value)
        return defaultValue;
    const num = parseInt(value, 10);
    return isNaN(num) ? defaultValue : num;
}
export function createAzureADConfig() {
    const clientId = getEnvVar(AUTH_ENV_VARS.AZURE_AD_CLIENT_ID, '');
    const tenantId = getEnvVar(AUTH_ENV_VARS.AZURE_AD_TENANT_ID, '');
    const enabled = getEnvBool(AUTH_ENV_VARS.AZURE_AD_ENABLED);
    if (!enabled || !clientId || !tenantId)
        return null;
    const authority = getEnvVar(AUTH_ENV_VARS.AZURE_AD_AUTHORITY, `https://login.microsoftonline.com/${tenantId}`);
    return {
        provider: AuthProvider.AZURE_AD,
        clientId,
        clientSecret: getEnvVar(AUTH_ENV_VARS.AZURE_AD_CLIENT_SECRET, ''),
        authority,
        redirectUri: getEnvVar(AUTH_ENV_VARS.AZURE_AD_REDIRECT_URI, `${getEnvVar('FRONTEND_URL', 'http://localhost:3000')}/auth/callback/azure-ad`),
        postLogoutRedirectUri: getEnvVar(AUTH_ENV_VARS.AZURE_AD_POST_LOGOUT_REDIRECT_URI, `${getEnvVar('FRONTEND_URL', 'http://localhost:3000')}/auth/logout`),
        scopes: getEnvVar(AUTH_ENV_VARS.AZURE_AD_SCOPES, 'openid,profile,email,User.Read').split(','),
        enabled: true,
        displayName: 'Microsoft Azure AD',
        iconUrl: '/icons/microsoft.svg',
        tenantId,
    };
}
export function createGoogleConfig() {
    const clientId = getEnvVar(AUTH_ENV_VARS.GOOGLE_CLIENT_ID, '');
    const enabled = getEnvBool(AUTH_ENV_VARS.GOOGLE_ENABLED);
    if (!enabled || !clientId)
        return null;
    return {
        provider: AuthProvider.GOOGLE,
        clientId,
        clientSecret: getEnvVar(AUTH_ENV_VARS.GOOGLE_CLIENT_SECRET, ''),
        authority: 'https://accounts.google.com',
        redirectUri: getEnvVar(AUTH_ENV_VARS.GOOGLE_REDIRECT_URI, `${getEnvVar('FRONTEND_URL', 'http://localhost:3000')}/auth/callback/google`),
        scopes: ['openid', 'profile', 'email'],
        enabled: true,
        displayName: 'Google',
        iconUrl: '/icons/google.svg',
        hostedDomain: getEnvVar(AUTH_ENV_VARS.GOOGLE_HOSTED_DOMAIN, ''),
    };
}
export function createGitHubConfig() {
    const clientId = getEnvVar(AUTH_ENV_VARS.GITHUB_CLIENT_ID, '');
    const enabled = getEnvBool(AUTH_ENV_VARS.GITHUB_ENABLED);
    if (!enabled || !clientId)
        return null;
    return {
        provider: AuthProvider.GITHUB,
        clientId,
        clientSecret: getEnvVar(AUTH_ENV_VARS.GITHUB_CLIENT_SECRET, ''),
        authority: 'https://github.com/login/oauth',
        redirectUri: getEnvVar(AUTH_ENV_VARS.GITHUB_REDIRECT_URI, `${getEnvVar('FRONTEND_URL', 'http://localhost:3000')}/auth/callback/github`),
        scopes: ['user:email', 'read:user'],
        enabled: true,
        displayName: 'GitHub',
        iconUrl: '/icons/github.svg',
        allowSignup: getEnvBool(AUTH_ENV_VARS.GITHUB_ALLOW_SIGNUP, true),
    };
}
export function loadAuthConfig() {
    const providers = [];
    const azureAD = createAzureADConfig();
    if (azureAD)
        providers.push(azureAD);
    const google = createGoogleConfig();
    if (google)
        providers.push(google);
    const github = createGitHubConfig();
    if (github)
        providers.push(github);
    const defaultProvider = providers.length > 0 ? providers[0].provider : undefined;
    return {
        providers,
        defaultProvider,
        allowMultipleProviders: true,
        sessionTimeout: getEnvNumber(AUTH_ENV_VARS.SESSION_TIMEOUT, 480),
        refreshTokenRotation: true,
        requireEmailVerification: getEnvBool(AUTH_ENV_VARS.REQUIRE_EMAIL_VERIFICATION, false),
        autoCreateUsers: getEnvBool(AUTH_ENV_VARS.AUTO_CREATE_USERS, true),
        defaultUserRole: getEnvVar(AUTH_ENV_VARS.DEFAULT_USER_ROLE, 'DEVELOPER'),
        jwtSecret: getEnvVar(AUTH_ENV_VARS.JWT_SECRET, 'your-super-secret-jwt-key-change-in-production'),
        jwtExpiresIn: getEnvVar(AUTH_ENV_VARS.JWT_EXPIRES_IN, '1h'),
        refreshTokenExpiresIn: getEnvVar(AUTH_ENV_VARS.REFRESH_TOKEN_EXPIRES_IN, '7d'),
    };
}
export function getProviderConfig(provider, config) {
    return config.providers.find(p => p.provider === provider) || null;
}
export function isProviderEnabled(provider, config) {
    const providerConfig = getProviderConfig(provider, config);
    return providerConfig?.enabled || false;
}
export function getEnabledProviders(config) {
    return config.providers.filter(p => p.enabled);
}
//# sourceMappingURL=auth.js.map